import {
	HttpErrorResponse,
	HttpResponse
}                                   from '@angular/common/http';
import { Injectable }               from '@angular/core';
import { IList }                    from '@app/core/interfaces/list-response.interfaces';
import { LoggerService }            from '@app/shared/services/logger.service';
import {
	RequestActionEnum,
	RequestCompleteEntityState,
	RequestCompleteListState,
	RequestState
}                                   from '@core/interfaces/api.interface';
import { LIST_OPERATIONS_METADATA } from '@shared/list-operations/config/list-operations.config';
import { IPaging }                  from '@shared/list-operations/model/paging.model';
import {
	Observable,
	of
}                                   from 'rxjs';

@Injectable( {
	providedIn: 'root'
} )
export class NormalizerService {

	constructor(
		private readonly logger: LoggerService
	) { }


	private updateEntityModelsRequestState<T>( entity: T, requestState: RequestState ): T {
		if ( entity.hasOwnProperty( 'uiRequestState' ) ) {
			entity = { ...entity, uiRequestState: requestState };
		}
		return entity;
	}


	private updateEntitiesModelsRequestState<T>( entities: T[], requestState: RequestState ): T[] {
		return entities.map( ( entity: T ) =>
			this.updateEntityModelsRequestState<T>( entity, requestState )
		);
	}

	private responseToRequestCompleteListState<T>(
		res: IList<T> | HttpResponse<T> | HttpErrorResponse | ErrorEvent,
		requestAction: RequestActionEnum,
		throwError = false
	): RequestCompleteListState<T> {

		let requestCompleteListState: RequestCompleteListState<T> = {
			action: requestAction,
			requestState: null,
			list: [],
			paging: {
				pageCount: 0,
				pageNumber: 0,
				pageSize: LIST_OPERATIONS_METADATA?.default?.paging?.pageSize ?? 20,
				totalElements: 0,
				blockOperationEmit: true
			},
			totalCount: undefined
		};


		// TypeError EVENT
		if ( res instanceof TypeError ) {
			this.logger.error( 'TypeError, you have an implementation error:', res );
			requestCompleteListState = Object.assign(
				{},
				requestCompleteListState,
				{ error: res, requestState: RequestState.FAILED }
			);
			if ( throwError ) {
				throw requestCompleteListState;
			} else {
				return requestCompleteListState;
			}
		}

		// ERROR EVENT
		if ( res instanceof ErrorEvent ) {
			this.logger.error( 'A client-side or network error occurred:', res.error.message );
			requestCompleteListState = Object.assign(
				{},
				requestCompleteListState,
				{ error: res, requestState: RequestState.FAILED }
			);
			if ( throwError ) {
				throw requestCompleteListState;
			} else {
				return requestCompleteListState;
			}
		}

		// HTTP ERROR RESPONSE
		if ( res instanceof HttpErrorResponse ) {
			requestCompleteListState = Object.assign(
				{},
				requestCompleteListState,
				{ error: res, requestState: RequestState.FAILED }
			);
			if ( throwError ) {
				throw requestCompleteListState;
			} else {
				return requestCompleteListState;
			}
		}

		// HTTP RESPONSE
		if ( res instanceof HttpResponse ) {
			requestCompleteListState = Object.assign(
				{},
				requestCompleteListState,
				{
					entities: res?.body ? res?.body : null,
					httpResponse: res,
					requestState: RequestState.SUCCESS
				}
			);
			return requestCompleteListState;
		}

		// uncomment in case of array type support is needed
		// ENTITIES RESPONSE
		// if ( Array.isArray( res ) ) {
		// 	const sanitizedList: T[] = res?.length ? res : [];
		// 	const sanitizedPaging: IPaging = {
		// 		pageCount: sanitizedList.length ? Math.ceil(sanitizedList.length/LIST_OPERATIONS_METADATA?.default?.paging?.pageSize ?? 20) : 0,
		// 		pageNumber: 0,
		// 		pageSize: LIST_OPERATIONS_METADATA?.default?.paging?.pageSize ?? 20
		// 	};
		//
		// 	requestCompleteListState = Object.assign(
		// 		{},
		// 		requestCompleteListState,
		// 		{ list: sanitizedList, paging: sanitizedPaging, requestState: RequestState.SUCCESS }
		// 	);
		// 	return requestCompleteListState;
		// }

		// IListResponse
		if ( typeof res === 'object' &&
				 res.hasOwnProperty( 'paging' ) &&
				 res.hasOwnProperty( 'results' )
		) {

			if ( res?.results?.length ) {
				const sanitizedList: T[] = this.updateEntitiesModelsRequestState<T>( res.results, RequestState.SUCCESS );
				const sanitizedPaging: IPaging = {
					pageCount: res?.paging?.pageCount ? res.paging.pageCount : 0,
					pageNumber: Number.isInteger( res?.paging?.pageNumber )
											? res.paging.pageNumber
											: sanitizedList.length
												? Math.ceil( sanitizedList.length / (
								LIST_OPERATIONS_METADATA?.default?.paging?.pageSize ?? 20
							) ) : 0,
					pageSize: res?.paging?.pageSize ? res.paging.pageSize : (
						LIST_OPERATIONS_METADATA?.default?.paging?.pageSize ?? 20
					),
					totalElements: res?.paging?.totalElements ? res.paging?.totalElements : 0,
					uiListResultIsSmallerAsPageSize: res.results.length < (
						res.paging.pageSize ?? LIST_OPERATIONS_METADATA?.default?.paging?.pageSize ?? 20
					),
					blockOperationEmit: true
				};

				requestCompleteListState = Object.assign(
					{},
					requestCompleteListState,
					{
						list: sanitizedList,
						paging: sanitizedPaging,
						requestState: RequestState.SUCCESS,
						totalCount: res?.totalCount ?? null
					}
				);
				return requestCompleteListState;
			} else {
				requestCompleteListState = Object.assign(
					{},
					requestCompleteListState,
					{
						list: [],
						paging: {
							pageCount: 0,
							pageNumber: 0,
							pageSize: LIST_OPERATIONS_METADATA?.default?.paging?.pageSize ?? 20,
							totalElements: 0,
							blockOperationEmit: true
						},
						requestState: RequestState.NO_RESULT
					}
				);
				return requestCompleteListState;
			}
		}

		// // SOMETHING UNEXPECTED
		// requestCompleteListState = Object.assign(
		// 	{},
		// 	requestCompleteListState,
		// 	{
		// 		requestState: RequestState.UNKNOWN,
		// 		debug: { message: `Received unexpected API list response.`, response: res }
		// 	}
		// );
		// if ( throwError ) {
		// 	throw requestCompleteListState;
		// } else {
		// 	return requestCompleteListState;
		// }
	}

	private responseToRequestCompleteEntityState<T>(
		res: T | HttpResponse<T> | HttpErrorResponse | ErrorEvent,
		requestAction: RequestActionEnum,
		throwError = false
	): RequestCompleteEntityState<T> {
		let requestCompleteEntityState: RequestCompleteEntityState<T> = {
			action: requestAction,
			requestState: null,
			entity: null
		};

		// TypeError EVENT
		if ( res instanceof TypeError ) {
			this.logger.error( 'TypeError, you have an implementation error:', res );
			requestCompleteEntityState = Object.assign(
				{},
				requestCompleteEntityState,
				{ error: res, requestState: RequestState.FAILED }
			);
			if ( throwError ) {
				throw requestCompleteEntityState;
			} else {
				return requestCompleteEntityState;
			}
		}

		// ERROR EVENT
		if ( res instanceof ErrorEvent ) {
			this.logger.error( 'A client-side or network error occurred:', res.error.message );
			requestCompleteEntityState = Object.assign(
				{},
				requestCompleteEntityState,
				{ error: res, requestState: RequestState.FAILED }
			);
			if ( throwError ) {
				throw requestCompleteEntityState;
			} else {
				return requestCompleteEntityState;
			}
		}

		// HTTP ERROR RESPONSE
		if ( res instanceof HttpErrorResponse ) {
			requestCompleteEntityState = Object.assign(
				{},
				requestCompleteEntityState,
				{ error: res, requestState: RequestState.FAILED }
			);
			if ( throwError ) {
				throw requestCompleteEntityState;
			} else {
				return requestCompleteEntityState;
			}
		}


		// HTTP RESPONSE
		if ( res instanceof HttpResponse ) {
			requestCompleteEntityState = Object.assign(
				{},
				requestCompleteEntityState,
				{
					entity: res?.body ? res?.body : null,
					httpResponse: res,
					requestState: RequestState.SUCCESS
				}
			);
			return requestCompleteEntityState;
		}

		// ENTITY
		if ( res ) {
			const sanitizedValue: T = res
																? this.updateEntityModelsRequestState<T>( res, RequestState.SUCCESS )
																: null;

			if ( sanitizedValue ) {
				requestCompleteEntityState = Object.assign(
					{},
					requestCompleteEntityState,
					{ entity: sanitizedValue, requestState: RequestState.SUCCESS }
				);

				return requestCompleteEntityState;
			}
		} else {
			requestCompleteEntityState = Object.assign(
				{},
				requestCompleteEntityState,
				{ entity: null, requestState: RequestState.NO_RESULT }
			);

			return requestCompleteEntityState;
		}

		// // SOMETHING UNEXPECTED
		// requestCompleteEntityState = Object.assign(
		// 	{},
		// 	requestCompleteEntityState,
		// 	{
		// 		requestState: RequestState.UNKNOWN,
		// 		debug: { message: `Received unexpected API entity response.`, response: res }
		// 	}
		// );
		// if ( throwError ) {
		// 	throw requestCompleteEntityState;
		// } else {
		// 	return requestCompleteEntityState;
		// }
	}


	// STORE HANDLER #########################################################################
	handleStoreListResponse<T>( list: T[] ): RequestCompleteListState<T> {
		const iList: IList<T> = { results: list, paging: null };
		return this.handleListSuccessResponse<T>( iList, RequestActionEnum.LIST );
	}

	handleStoreEntityResponse<T>( entity: T, requestAction: RequestActionEnum ): RequestCompleteEntityState<T> {
		return this.handleEntitySuccessResponse<T>( entity, requestAction );
	}


	// API HANDLER ###########################################################################
	handleListSuccessResponse<T>(
		res: IList<T>,
		requestAction: RequestActionEnum
	): RequestCompleteListState<T> {
		return this.responseToRequestCompleteListState<T>( res, requestAction );
	}

	handleListErrorResponse<T>(
		res: IList<T> | HttpResponse<T> | HttpErrorResponse | ErrorEvent,
		requestAction: RequestActionEnum,
		throwError = false
	): Observable<RequestCompleteListState<T>> {
		return of( this.responseToRequestCompleteListState<T>( res, requestAction, throwError ) );
	}

	handleEntitySuccessResponse<T>(
		res: T | HttpResponse<T>,
		requestAction: RequestActionEnum
	): RequestCompleteEntityState<T> {
		return this.responseToRequestCompleteEntityState<T>( res, requestAction );
	}

	handleEntityErrorResponse<T>(
		res: T | HttpResponse<T> | HttpErrorResponse | ErrorEvent,
		requestAction: RequestActionEnum,
		throwError = false
	): Observable<RequestCompleteEntityState<T>> {
		return of( this.responseToRequestCompleteEntityState<T>( res, requestAction, throwError ) );
	}
}
